import type {
  CorpCatDto,
  FacilityDto,
  FacilitySetupStatus,
  SkillTypeDto,
} from "@ckweb/ckfetch/connectables/setup";
import type { FacilityUpsertPayload } from "@/types/wizard";

const fixedFacilitySteps = [
  {
    id: "intro",
    url: "intro",
    hideSidebar: true,
  },
  {
    id: "info",
    title: "cksetup.wizard.facilityInfo",
    url: "info",
  },
  // TODO: uncomment when we decide is useful
  // {
  //   id: "productCategories",
  //   title: "cksetup.wizard.productCategories",
  //   url: "product-categories",
  //   validate: (facility?: Partial<FacilityDto>) =>
  //     !!facility?.productCategories?.length,
  // },
  {
    id: "rooms",
    title: "cksetup.wizard.rooms",
    url: "rooms",
    optional: true,
  },
  {
    id: "infrastructureCleaning",
    title: "cksetup.wizard.cleaningInfrastructure",
    url: "cleaning-infrastructure",
    validate: (facility?: Partial<FacilityDto>) =>
      !!facility?.infrastructureCleaningRoles?.length,
  },
  {
    id: "equipmentCleaning",
    title: "cksetup.wizard.cleaningEquipment",
    url: "cleaning-equipment",
    validate: (facility?: Partial<FacilityDto>) =>
      !!facility?.equipmentCleaningRoles?.length,
  },
  {
    id: "otherCleaning",
    title: "cksetup.wizard.cleaningOther",
    url: "cleaning-other",
    validate: (facility?: Partial<FacilityDto>) =>
      !!facility?.routineCleaningRoles?.length,
  },
  {
    id: "allergens",
    title: "cksetup.wizard.allergens",
    url: "allergens",
    optional: true,
  },
  {
    id: "skills",
    title: "cksetup.wizard.skills",
    url: "skills",
    validate: (facility?: Partial<FacilityDto>) =>
      !!facility?.skillTypes?.length,
  },
  {
    id: "corporateCategories",
    title: "cksetup.wizard.corporateKPIs",
    url: "corporate-kpis",
    validate: (facility?: Partial<FacilityDto>) =>
      !!facility?.corporateCategories?.length,
  },
  {
    id: "done",
    url: "done",
    hideSidebar: true,
  },
] as const;

export type FacilityStepId = (typeof fixedFacilitySteps)[number]["id"];
export type FacilityStep = WizardStep & {
  id: FacilityStepId;
  index: number;
  optional?: boolean;
  validate?: (facility?: Partial<FacilityDto>) => boolean;
};

export const facilitySteps = fixedFacilitySteps.map((step, index) => ({
  ...step,
  index,
})) as FacilityStep[];

export function calculateFacilityStatus(
  facility: Readonly<Partial<FacilityDto>>,
  importsCompleted: boolean = false,
): FacilitySetupStatus {
  if (!facility.setupStatus) {
    return "new";
  }

  if (facility.setupStatus === "unknown") {
    if (facility.corporateCategories?.length && !importsCompleted) {
      return "dataImportInProgress";
    }
    if (facility.corporateCategories?.length && importsCompleted) {
      return "setupComplete";
    }
    return "new";
  }

  if (facility.setupStatus === "new" && facility.corporateCategories?.length) {
    return "dataImportInProgress";
  }

  if (facility.setupStatus === "dataImportInProgress" && importsCompleted) {
    return "setupComplete";
  }
  return facility.setupStatus;
}

function validateStep(
  step: FacilityStep | undefined,
  facility?: Partial<FacilityDto>,
) {
  return !step?.validate || step.validate(facility);
}

export function calculateFacilityLatestCompletedStepId(
  facility?: Partial<FacilityDto>,
): FacilityStepId {
  if (!facility?.id) return "intro";
  if (facility.setupStatus !== "new") return "info";

  const latestCompletedStep = facilitySteps.find(
    (step, index) =>
      // First invalid step or
      // First valid step where the next step is not valid
      !validateStep(step, facility) ||
      !validateStep(facilitySteps[index + 1], facility),
  );

  return latestCompletedStep?.id ?? "corporateCategories";
}

export function calculateFacilityLatestStep(facility?: Partial<FacilityDto>) {
  const latestCompletedStepId =
    calculateFacilityLatestCompletedStepId(facility);
  const latestCompletedStepIndex = facilitySteps.findIndex(
    (step) => step.id === latestCompletedStepId,
  );

  if (facility?.setupStatus && facility.setupStatus !== "new") {
    return {
      latestCompletedStep: facilitySteps[latestCompletedStepIndex]!,
      latestAccessibleStep: facilitySteps[facilitySteps.length - 1]!,
    };
  }

  const latestAccessibleStepIndex = facilitySteps.findIndex(
    (step, index) => index > latestCompletedStepIndex && !step.optional,
  );

  return {
    latestCompletedStep: facilitySteps[latestCompletedStepIndex]!,
    latestAccessibleStep: facilitySteps[latestAccessibleStepIndex]!,
  };
}

export function normalizeFacilityPayload(facility: FacilityUpsertPayload) {
  return {
    ...facility,
    // Organization needs to be undefined otherwise we get a server error
    organizationId: facility.organizationId ?? undefined,
    setupStatus: calculateFacilityStatus(facility),
    skillTypes:
      facility.skillTypes?.map(
        ({ code, name, active, isDefault }) =>
          ({ code, name, active, isDefault }) as SkillTypeDto,
      ) || [],
    corporateCategories:
      facility.corporateCategories?.map(
        ({ number, name, active }) => ({ number, name, active }) as CorpCatDto,
      ) || [],
  } as FacilityUpsertPayload;
}

export function facilityDtoDefaults(): FacilityUpsertPayload {
  return {
    organizationId: null,
    minimumBuildings: 0,
    maximumBuildings: 0,
    minimumRoomsAreas: 0,
    maximumRoomsAreas: 0,
    productCategories: [],
    skillTypes: [],
    corporateCategories: [],
    infrastructureCleaningMethods: [],
    useChangeOvers: true,
  };
}

export function makeFacilitySortFunctions() {
  return {
    id: {
      ascending: (a: FacilityDto, b: FacilityDto) => a.id - b.id,
      descending: (a: FacilityDto, b: FacilityDto) => b.id - a.id,
    },
    facility: {
      ascending: (a: FacilityDto, b: FacilityDto) =>
        a.name!.localeCompare(b.name!),
      descending: (a: FacilityDto, b: FacilityDto) =>
        b.name!.localeCompare(a.name!),
    },
    organization: {
      ascending: (a: FacilityDto, b: FacilityDto) => {
        const aName = a.organization?.name ?? "";
        const bName = b.organization?.name ?? "";
        return aName.localeCompare(bName);
      },
      descending: (a: FacilityDto, b: FacilityDto) => {
        const aName = a.organization?.name ?? "";
        const bName = b.organization?.name ?? "";
        return bName.localeCompare(aName);
      },
    },
    status: {
      ascending: (a: FacilityDto, b: FacilityDto) =>
        a.setupStatus.localeCompare(b.setupStatus),
      descending: (a: FacilityDto, b: FacilityDto) =>
        b.setupStatus.localeCompare(a.setupStatus),
    },
  };
}

export const facilityStatusList: {
  value: FacilitySetupStatus;
  themeColor: ThemeColor;
  text: string;
  actionText: string;
}[] = [
  {
    value: "new",
    themeColor: "primary",
    text: "cksetup.facilities.statusNew",
    actionText: "cksetup.facilities.actionNew",
  },
  {
    value: "dataImportInProgress",
    themeColor: "warning",
    text: "cksetup.facilities.statusImporting",
    actionText: "cksetup.facilities.actionImporting",
  },
  {
    value: "setupComplete",
    themeColor: "success",
    text: "cksetup.facilities.statusComplete",
    actionText: "cksetup.facilities.actionComplete",
  },
];
